import { ApiClient } from "@ucl/library";
import { KeyPerson, KeyPersonApplicationRoleMetricsDTO } from "@/types/KeyPerson";

class KeyPersonApiClient {
  private _apiClient: ApiClient;

  constructor(apiClient: ApiClient) {
    this._apiClient = apiClient;
  }

  /**
   * Creates a new key person for a financial application.
   */
  createKeyPerson = async (financialApplicationId: string) => {
    return this._apiClient.post<KeyPerson>(
      `api/financial-application/${financialApplicationId}/key-people/`
    );
  };

  /**
   * Validates key people for a financial application.
   */
  validateKeyPeople = async (financialApplicationId: string) => {
    return this._apiClient.post<Record<string, Record<string, string[]>>>(
      `api/financial-application/${financialApplicationId}/key-people/validate`
    );
  };

  /**
   * Retrieves key people application role metrics for a financial application.
   */
  getKeyPeopleApplicationRoleMetrics = async (financialApplicationId: string) => {
    return this._apiClient.get<KeyPersonApplicationRoleMetricsDTO>(
      `api/financial-application/${financialApplicationId}/key-people/metrics`
    );
  };

  /**
   * Retrieves a key person by their ID.
   */
  getKeyPerson = async (financialApplicationId: string, keyPersonId: string) => {
    return this._apiClient.get<KeyPerson>(
      `api/financial-application/${financialApplicationId}/key-people/${keyPersonId}`
    );
  };

  /**
   * Retrieves all key people for a financial application.
   */
  getKeyPeople = async (financialApplicationId: string) => {
    return this._apiClient.get<KeyPerson[]>(
      `api/financial-application/${financialApplicationId}/key-people`
    );
  };

  getKeyPersonById = async (keyPersonId: string) => {
    return this._apiClient.get<KeyPerson>(`api/key-people/${keyPersonId}`);
  };

  /**
   * Updates an existing key person for a financial application.
   */
  updateKeyPerson = async (
    financialApplicationId: string,
    keyPersonId: string,
    data: KeyPerson
  ) => {
    return this._apiClient.put<KeyPerson>(
      `api/financial-application/${financialApplicationId}/key-people/${keyPersonId}`,
      data
    );
  };

  /**
   * Updates the identity documents for a key person.
   */
  updateKeyPersonIdentityDocuments = async (
    financialApplicationId: string,
    keyPersonId: string,
    data: KeyPerson
  ) => {
    return this._apiClient.put<KeyPerson>(
      `api/financial-application/${financialApplicationId}/key-people/${keyPersonId}/documents`,
      data
    );
  };

  /**
   * Deletes a key person from a financial application.
   */
  deleteKeyPerson = async (financialApplicationId: string, keyPersonId: string) => {
    return this._apiClient.delete<KeyPerson>(
      `api/financial-application/${financialApplicationId}/key-people/${keyPersonId}`
    );
  };

  /**
   * Retrieves a list of key person IDs for a financial application.
   */
  getKeyPersonIds = async (financialApplicationId: string) => {
    return this._apiClient.get<string[]>(
      `api/financial-application/${financialApplicationId}/key-people-ids`
    );
  };
}

export default KeyPersonApiClient;
